import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Moment } from 'moment';
import {
    burstActionCount,
    GroupedActivityLog,
} from '../../utils/groupActivityLogs';
import { Icon } from '../Icon';

export type ActivityBurstRowProps = {
    entry: GroupedActivityLog;
    summaryHtml: string;
    showProjectBadge?: boolean;
    describeMember?: (member: GroupedActivityLog) => string;
    renderTimestamp: (createdAt: Moment) => React.ReactNode;
};

function ActivityBurstRow(props: ActivityBurstRowProps) {
    const {
        entry,
        summaryHtml,
        showProjectBadge = false,
        describeMember,
        renderTimestamp,
    } = props;
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const actionCount = burstActionCount(entry);
    const log = entry.log;

    return (
        <div data-testid="activity-burst">
            <div className="flex items-start justify-between gap-2 text-sm">
                <div>
                    <button
                        type="button"
                        className="inline-flex items-start gap-1 text-left hover:underline"
                        aria-expanded={open}
                        data-testid="activity-burst-toggle"
                        onClick={() => setOpen((value) => !value)}
                    >
                        <Icon
                            type={open ? 'down' : 'right'}
                            size={4}
                            className="mt-0.5 text-gray-500 dark:text-gray-400"
                        />
                        <span dangerouslySetInnerHTML={{ __html: summaryHtml }} />
                    </button>
                    <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                        {t('logs.burst_actions', { count: actionCount })}
                    </span>
                </div>
                {showProjectBadge && log.project?.name && (
                    <span
                        className="shrink-0 rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300"
                        data-testid="activity-project-badge"
                    >
                        {log.project.name}
                    </span>
                )}
            </div>
            <div className="text-xs italic text-gray-500 dark:text-gray-400">
                {renderTimestamp(entry.createdAt)}
            </div>
            {open && entry.members && (
                <ul
                    className="mt-2 space-y-1.5 border-l border-gray-200 dark:border-gray-700 pl-3"
                    data-testid="activity-burst-details"
                >
                    {entry.members.map((member, index) => (
                        <li
                            key={member.log._id || `${entry.log._id}-member-${index}`}
                            className="text-xs text-gray-600 dark:text-gray-300"
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: describeMember
                                        ? describeMember(member)
                                        : member.log.describe(/*fromOutside*/ false),
                                }}
                            />
                            {member.count > 1 && (
                                <span className="text-gray-500 dark:text-gray-400">
                                    {' '}
                                    {t('logs.repeat_count', { count: member.count })}
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default ActivityBurstRow;
